import React, { useState, useEffect, useCallback } from 'react';
import { Users, TrendingUp, Clock, Dice1, RefreshCw } from 'lucide-react';
import { surpriseAPI, SurpriseOverview, TopUser, SurpriseRoll } from '../api/services/surpriseService';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/common/PageHeader';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useNotification } from '../hooks/useNotification';
import { format } from 'date-fns';

export const SurpriseRollsPage: React.FC = () => {
  const [overview, setOverview] = useState<SurpriseOverview | null>(null);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [recentRolls, setRecentRolls] = useState<SurpriseRoll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userDetailsOpen, setUserDetailsOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<{
    user: {
      _id: string;
      name: string;
      email: string;
      reshufflesLeft: number;
      dailyLimit: number;
      todayUsed: number;
      totalReshufflesUsed: number;
      lastReshuffleDate?: string;
    };
    statistics: {
      totalRolls: number;
      completedRolls: number;
      expiredRolls: number;
      completionRate: string;
    };
    rollHistory: SurpriseRoll[];
  } | null>(null);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: '',
  });
  const { showNotification } = useNotification();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (dateFilter.startDate) params.startDate = dateFilter.startDate;
      if (dateFilter.endDate) params.endDate = dateFilter.endDate;

      const data = await surpriseAPI.getAdminStats(params);
      setOverview(data?.overview || null);
      setTopUsers(Array.isArray(data?.topUsers) ? data.topUsers : []);
      setRecentRolls(Array.isArray(data?.recentRolls) ? data.recentRolls : []);
      setError(null);
    } catch (error) {
      setError('Failed to fetch surprise roll statistics');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUserClick = async (userId: string) => {
    try {
      const data = await surpriseAPI.getUserStats(userId);
      setUserDetails(data);
      setUserDetailsOpen(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      showNotification('Failed to fetch user details', 'error');
    }
  };

  const handleDateFilterChange = () => {
    fetchData();
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (loading) return <DashboardLayout><LoadingState /></DashboardLayout>;
  if (error) return <DashboardLayout><ErrorState message={error} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <PageHeader
        title="Surprise Rolls"
        description="Monitor and analyze user surprise roll activity"
      />

      <div className="space-y-6">
        {/* Date Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="endDate">End Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                />
              </div>
              <Button onClick={handleDateFilterChange}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Apply Filter
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setDateFilter({ startDate: '', endDate: '' });
                }}
              >
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Overview Stats */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Rolls</CardTitle>
              <Dice1 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview?.totalRolls || 0}</div>
              <p className="text-xs text-muted-foreground">
                Today: {overview?.todayRolls || 0}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview?.completionRate || 0}%</div>
              <p className="text-xs text-muted-foreground">
                {overview?.completedRolls || 0} completed / {overview?.expiredRolls || 0} expired
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview?.activeUsers || 0}</div>
              <p className="text-xs text-muted-foreground">
                Daily limit: {overview?.dailyLimit || 0} rolls
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatDuration(overview?.avgDurationSeconds || 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                Time to complete roll
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Top Users */}
        <Card>
          <CardHeader>
            <CardTitle>Top Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topUsers.map((user, index) => (
                <div
                  key={user.userId}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleUserClick(user.userId)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-600">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{user.userName}</p>
                      <p className="text-sm text-gray-500">{user.userEmail}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold">{user.rollCount} rolls</p>
                      <p className="text-sm text-gray-500">{user.completedCount} completed</p>
                    </div>
                    <Badge variant={user.completedCount === user.rollCount ? "default" : "secondary"}>
                      {Math.round((user.completedCount / user.rollCount) * 100)}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Rolls */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Rolls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b">
                    <th className="pb-2">User</th>
                    <th className="pb-2">Selected User</th>
                    <th className="pb-2">Status</th>
                    <th className="pb-2">Duration</th>
                    <th className="pb-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRolls.map((roll) => (
                    <tr key={roll._id} className="border-b">
                      <td className="py-3">
                        <button
                          className="text-blue-600 hover:underline"
                          onClick={() => handleUserClick(typeof roll.user_id === 'string' ? roll.user_id : roll.user_id._id)}
                        >
                          {typeof roll.user_id === 'object' ? `${roll.user_id.first_name} ${roll.user_id.last_name}` : roll.user_id}
                        </button>
                      </td>
                      <td className="py-3">
                        {roll.selected_user_id ? (
                          typeof roll.selected_user_id === 'object' ? (
                            <>{roll.selected_user_id.first_name} {roll.selected_user_id.last_name}</>
                          ) : (
                            <span>{roll.selected_user_id}</span>
                          )
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3">
                        <Badge
                          variant={
                            roll.status === 'completed'
                              ? 'default'
                              : roll.status === 'expired'
                              ? 'destructive'
                              : 'secondary'
                          }
                        >
                          {roll.status}
                        </Badge>
                      </td>
                      <td className="py-3">
                        {roll.duration ? formatDuration(Math.round(roll.duration / 1000)) : '-'}
                      </td>
                      <td className="py-3">
                        {format(new Date(roll.createdAt), 'MMM dd, yyyy HH:mm')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* User Details Dialog */}
      <Dialog open={userDetailsOpen} onOpenChange={setUserDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Surprise Roll Details</DialogTitle>
            <DialogDescription>
              Detailed statistics and history for the selected user
            </DialogDescription>
          </DialogHeader>
          {userDetails && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">User Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium">{userDetails.user.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{userDetails.user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Reshuffles Left Today</p>
                    <p className="font-medium">
                      {userDetails.user.reshufflesLeft} / {userDetails.user.dailyLimit}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Today's Usage</p>
                    <p className="font-medium">{userDetails.user.todayUsed}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Reshuffles Used</p>
                    <p className="font-medium">{userDetails.user.totalReshufflesUsed}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Last Reshuffle</p>
                    <p className="font-medium">
                      {userDetails.user.lastReshuffleDate
                        ? format(new Date(userDetails.user.lastReshuffleDate), 'MMM dd, yyyy')
                        : 'Never'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-2">Statistics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Rolls</p>
                    <p className="font-medium">{userDetails.statistics.totalRolls}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="font-medium">{userDetails.statistics.completedRolls}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Expired</p>
                    <p className="font-medium">{userDetails.statistics.expiredRolls}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Completion Rate</p>
                    <p className="font-medium">{userDetails.statistics.completionRate}%</p>
                  </div>
                </div>
              </div>

              {/* Roll History */}
              <div>
                <h3 className="font-semibold mb-2">Roll History</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {userDetails.rollHistory.map((roll: SurpriseRoll) => (
                    <div key={roll._id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">
                            {roll.selected_user_id
                              ? `Selected: ${typeof roll.selected_user_id === 'object' ? `${roll.selected_user_id.first_name} ${roll.selected_user_id.last_name}` : roll.selected_user_id}`
                              : 'No selection'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {format(new Date(roll.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                          </p>
                        </div>
                        <Badge
                          variant={
                            roll.status === 'completed'
                              ? 'default'
                              : roll.status === 'expired'
                              ? 'destructive'
                              : 'secondary'
                          }
                        >
                          {roll.status}
                        </Badge>
                      </div>
                      {roll.duration && (
                        <p className="text-sm text-gray-500 mt-1">
                          Duration: {formatDuration(Math.round(roll.duration / 1000))}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};